import { Component, Inject, OnInit, Input, ViewChild, ElementRef, ChangeDetectorRef, Injectable } from '@angular/core';
import { Project } from '@app/models/project';
import { SiteProvisionStatus } from '@app/common/site-provision-status';
import { fromEvent, of, Observable, Subject, concat, combineLatest, from } from 'rxjs';
import { ProjectService } from '@app/services/project.service';
import { map, debounceTime, distinctUntilChanged, delay, concatMap, first, switchMap, tap } from 'rxjs/operators';
import { Environment } from '@environments/interface.environment';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { O365GroupService } from '@app/services/o365group.service';
import { UserService } from '@app/services/user.service';
import { Constants } from '@app/common/constants';
import { MatTableDataSource } from '@angular/material/table';
import { ProjectQuery } from '@app/models/project-query';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ProjectStatus } from '@app/common/project-status';
import { User as UserModel } from '@app/models/user';
import { Utilities } from '@app/common/utilities';
import { MatSort } from '@angular/material/sort';
import { User as GraphUser } from '@microsoft/microsoft-graph-types';
import { FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
//import {MAT_MOMENT_DATE_FORMATS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter} from '@angular/material/core';

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  format(date: Date, displayFormat: { year?: string; month?: string; day?: string }): string {
    if (displayFormat.day === 'numeric' && displayFormat.month === 'numeric' && displayFormat.year === 'numeric') {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      return (year + "-" + month.toString().padStart(2, '0') + "-" + day.toString().padStart(2, '0'));
    }
    return super.format(date, displayFormat);
  }
  getFirstDayOfWeek(): number {
    return 1; // return Monday as the fist day of the week
  }
}

@Component({
  selector: 'app-project-list',
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ]
})
export class ProjectListComponent implements OnInit {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    private projectService: ProjectService,
    private groupService: O365GroupService,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
    private adapter: DateAdapter<any>
    ) {}

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.projectsDataSource.sort = sort;

    this.projectsDataSource.sortingDataAccessor = (row:Project, columnName:string) : string => {
      if(columnName == "ProjectManager") {
        let upns = "";
        row.Managers.forEach(pm => upns += pm.UpnId);
        return upns?.toLowerCase();
      }
      if(columnName === "Retention") {
        if(row.Retention === -1) return "99" // forever
      }
      if(typeof row[columnName] === "number") {
        return String(row[columnName]).padStart(2, '0');  // zero padding
      }
      if(!!row[columnName]) {
        return row[columnName].toString().toLowerCase();
      } else {
        return "";
      }      
    }
  }


  dataLoading = false;

  @Input() public view: string; // "all", "managed", "my"
  projectsDataSource: MatTableDataSource<Project> = new MatTableDataSource<Project>();
  displayedColumns: string[] = ['EditProjectMenu', 'ProjectId', 'Name', 'InfoClass', 'ProjectManager', 'CostCenter', 'Customer', 'StartDate', 'EndDate', 'Retention' , 'action'];
  currentPageSize = 5;


  showNoResultLabel = false;
  showMoreResultsAvailableLabel = false;
  queryLimit = 300;

  showAdvancedSearch = false;

  // Query Filters
  searchValue = '';
  showInactive = false;
  showSubProjects = false;

  // Advanced Search
  advancedSearchButtonEnabled = false;
  searchFieldProjectId = '';
  searchFieldName = '';
  searchFieldDescription = '';
  searchFieldCostCenter = '';
  searchFieldCustomer = '';
  searchFieldInfoClass: string = undefined;
  searchFieldRetention: string = undefined;
  searchFieldStartDateFrom: Date = undefined;
  searchFieldStartDateTo: Date = undefined;
  searchFieldEndDateFrom: Date = undefined;
  searchFieldEndDateTo: Date = undefined;
  searchFieldActivityStatus: string = undefined;
  searchFieldHierarchical: string = undefined;
  searchFieldManager: GraphUser = undefined;

  managerSearch_IsSearching = false;
  managerSearch_NothingFound = false;
  managerSearch_FoundUsers$: Observable<Array<GraphUser>>;
  managerControl = new FormControl()
  getDisplayName(user: GraphUser) {
    return user?.displayName ? user.displayName : "";
  }

  user: UserModel;

  oldProjectToolTipText: string


  ngOnInit() {

    //this.adapter.setLocale('sv');


    // Setup Data Table

    this.projectsDataSource.paginator = this.paginator;

    // Paginator does not support localization, so we need a little hack
    if (Utilities.getUsersLocale('sv') === 'sv') {
      this.projectsDataSource.paginator._intl.itemsPerPageLabel = 'Projekt per sida:';
      this.projectsDataSource.paginator._intl.getRangeLabel = function (page, pageSize, length) {
        return length + ' Projekt     (Sida ' + (page + 1) + ' av ' + Math.ceil(length / pageSize) + ')';
      };
    } else {
      this.projectsDataSource.paginator._intl.itemsPerPageLabel = 'Projects per page:';
      this.projectsDataSource.paginator._intl.getRangeLabel = function (page, pageSize, length) {
        return length + ' Projects     (Page ' + (page + 1) + ' of ' + Math.ceil(length / pageSize) + ')';
      };
    }

    // Tooltips in multiple languages
    if (Utilities.getUsersLocale('sv') === 'sv') {
      this.oldProjectToolTipText = 'Gammalt projektnummer: ';
    } else {
      this.oldProjectToolTipText = 'Old project number: ';
    }


    if (localStorage.getItem("projectListPageSize") !== null) {
      this.currentPageSize = parseInt(localStorage.getItem("projectListPageSize"));
      this.projectsDataSource.paginator.pageSize = this.currentPageSize;
    } else {
      this.projectsDataSource.paginator.pageSize = this.currentPageSize;
    }

    // Setup views
    switch (this.view) {
      case 'my':
        this.showSubProjects = true;
        this.fetchProjects();
        break;

      case 'managed':
        this.fetchProjects();
        break;

      case 'all':
        break;
    }

    this.managerSearch_FoundUsers$ = this.managerControl
      .valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(searchTerm => {
          this.managerSearch_NothingFound = false;
          this.managerSearch_IsSearching = true;
          if (typeof searchTerm === 'string' && searchTerm.length > 0) {

            return from(
              this.userService.searchMsGraphForUsers(searchTerm, 5, true)
            ).pipe(
              map( (users) => {
                return users.filter(user => user.userPrincipalName != this.searchFieldManager?.userPrincipalName);
              }),
              tap((users) => {
                this.managerSearch_IsSearching = false;
                if (users.length === 0) {
                  this.managerSearch_NothingFound = true;
                }
              })
            );
          } else {
            this.managerSearch_IsSearching = false;
            return of(new Array<GraphUser>());
          }
        })
      );

  }

  @ViewChild('projectSearch', { static: false }) set delayContent(projectSearch: ElementRef) {

    if (!projectSearch) { return; } // projectSearch will be undefined on page load

    fromEvent(projectSearch.nativeElement, 'keyup').pipe(
      // get value
      map((evt: any) => evt.target.value),
      // emit after 1s of silence
      debounceTime(1000),
      // emit only if data changes since the last emit
      distinctUntilChanged(),
      delay(0)
    ).subscribe((text: string) => {
        if (text.length >= 2) {
          // this.viewContext.search = text;
          this.fetchProjects();
        }
        if (text.length === 0) {
          this.searchValue = '';
          this.fetchProjects();
        }

      });
  }

  fetchProjects() {

    if (!this.showAdvancedSearch && this.searchValue === '') {
      if (this.view === 'all') {
        this.projectsDataSource.data = [];
        return;
      }
    }

    this.dataLoading = true;
    this.showNoResultLabel = false;
    this.showMoreResultsAvailableLabel = false;

    this.projectsDataSource.data = [];

    const projectLoader = this.userService.getOrCreateMe().pipe(
      concatMap(myDetails => {
        this.user = myDetails;
        return this.groupService.getMyO365Groups(['id']).pipe(
          map(myGroups => ({
            myDetails: myDetails,
            myGroups: myGroups
          }))
        );
      }),
      concatMap(previousObservableResult => {
        // Construct Search query
        const q = new ProjectQuery();

        if (this.showAdvancedSearch) {
          q.ProjectId = this.searchFieldProjectId !== "" ? this.searchFieldProjectId?.trim() : undefined;
          q.Name = this.searchFieldName !== "" ? this.searchFieldName?.trim() : undefined;
          q.Description = this.searchFieldDescription !== "" ? this.searchFieldDescription?.trim() : undefined;
          q.ManagerUpn = this.searchFieldManager ? this.searchFieldManager.userPrincipalName : undefined;

          q.Customer = this.searchFieldCustomer !== "" ? this.searchFieldCustomer?.trim() : undefined;
          q.CostCenter = this.searchFieldCostCenter !== "" ? this.searchFieldCostCenter?.trim() : undefined;

          q.StartDateFrom = this.searchFieldStartDateFrom ? this.searchFieldStartDateFrom : undefined;
          q.StartDateTo = this.searchFieldStartDateTo ? this.searchFieldStartDateTo : undefined;
          q.EndDateFrom = this.searchFieldEndDateFrom ? this.searchFieldEndDateFrom : undefined;
          q.EndDateTo = this.searchFieldEndDateTo ? this.searchFieldEndDateTo : undefined;

          q.Hierarchical = this.searchFieldHierarchical ? this.searchFieldHierarchical : undefined;
          q.ActivityStatus = this.searchFieldActivityStatus ? this.searchFieldActivityStatus : undefined;
          q.InfoClass = this.searchFieldInfoClass ? this.searchFieldInfoClass : undefined;
          q.Retention = this.searchFieldRetention ? this.searchFieldRetention : undefined;

          q.SearchQuery = undefined;
        } else {
          q.ActivityStatus = this.showInactive === true ? undefined : "active";
          q.Hierarchical = this.showSubProjects === true ? undefined : "root";
          q.SearchQuery = this.searchValue.trim();
        }
    
        q.Limit = undefined;

        if (this.view === 'managed') {
          // Only show projects where I am one of the project managers in the project db.
          q.ManagerUpn = previousObservableResult.myDetails.UpnId;
        } else if (this.view === 'my') {
          // Only show projects where I am a member (or owner) on the O365 group.
          q.O365Groups = previousObservableResult.myGroups.map(m => m.id);
        } else if (this.view === 'all') {
          if(previousObservableResult.myDetails.Role !== Constants.PDP_ROLE_ADMIN) {
            q.Limit = this.queryLimit;
          }
        }

        return this.projectService.query(q);
      }),
      concatMap(fetched => {
        fetched.Projects.forEach(p => p.Managers.forEach(m => {
          m.MsGraphUser$ = this.userService.getSingleUserByUpn(m.UpnId);
        }));
        this.showMoreResultsAvailableLabel = fetched.MoreResultsAvailable;
        return of(fetched.Projects);
      })
    );

    projectLoader.pipe(first()).subscribe(
      searchResult => {
        this.projectsDataSource.data = searchResult;
        this.dataLoading = false;
        this.changeDetectorRef.detectChanges();
        if (this.projectsDataSource.data.length === 0) { this.showNoResultLabel = true; }
      },
      error => {
        this.dataLoading = false;
        this.changeDetectorRef.detectChanges();
        console.log('error', error);
      }
    );

  }

  showProjectContextMenu(project: Project) {
    // If adding more than edit retention & infoclass this should be revised to ensure that rules are correct
    if (this.showManagerActions(project)) {
      if (project.SiteProvisionStatus === SiteProvisionStatus.Idle || project.SiteProvisionStatus === SiteProvisionStatus.Closed) {
        return true;
      }
    }
    return false;
  }

  getSiteStatus(project: Project) {
    /*if (project.O365GroupId === null) {
      return '';
    }*/

    if (this.showManagerActions(project)) {
      if (this.canAddGroupToProject(project)) {
        return 'canconnect';
      }
    }

    if (project.O365GroupId === null) {
      return '';
    }

    if (project.SiteProvisionStatus === SiteProvisionStatus.TemplateRequested || project.SiteProvisionStatus === SiteProvisionStatus.TemplateProcessing) {
      if (project.AppliedSiteTemplates === null) {
        // only show "site is being prepared" if processing a project site with no previous template applied (ie first time)
        return 'preparing';
      }
    }
    
    if (project.SiteProvisionStatus === SiteProvisionStatus.CloseRequested || project.SiteProvisionStatus === SiteProvisionStatus.CloseProcessing) {
      return 'closing';
    }
    if (project.SiteProvisionStatus === SiteProvisionStatus.Closed) {
      return 'closed';
    }

    if (project.SiteProvisionStatus === SiteProvisionStatus.ArchiveRequested || project.SiteProvisionStatus === SiteProvisionStatus.ArchiveProcessing) {
      return 'archiving';
    }
    if (project.SiteProvisionStatus === SiteProvisionStatus.Archived) {
      return 'archived';
    }

    if (project.SiteUrl !== null) {
      return 'ongoing';
    }
    return '';
  }

  makeLinkToSharePoint(o365GroupId) {
    const rootUrl = this.environment.tenant;
    return rootUrl + '/_layouts/groupstatus.aspx?id=' + o365GroupId + '&target=' + Constants.COLLAB_UNIFIED_GROUPS_LINK_TARGET;
  }

  isValidDate(date) {
    if (!date || date === '0001-01-01T00:00:00') {
      return false;
    }
    return true;
  }

  refreshData() {
    this.groupService.flushCache();
    this.projectService.flushCache();
    this.fetchProjects();
  }

  clearSearch() {
    this.searchValue = '';
    this.fetchProjects();
  }

  showManagerActions(project: Project) {

    const found = project.Managers.find(m => m.UpnId === this.user.UpnId);
    if (found) {
      return true;
    } else {
      return false;
    }
  }

  canAddGroupToProject(project: Project) {
    const status = project.ProjectStatus ? project.ProjectStatus.toLowerCase() : '';

    if (status === ProjectStatus.Approved || status === ProjectStatus.Started || status === ProjectStatus.Completed || status === ProjectStatus.Closed) {
      if (project.O365GroupId === null) {
        return true;
      }
    }

    return false;
  }

  canCloseProject(project: Project) {
    const status = project.ProjectStatus ? project.ProjectStatus.toLowerCase() : '';

    if (status === ProjectStatus.Approved || status === ProjectStatus.Started || status === ProjectStatus.Completed || status === ProjectStatus.Closed) {
      if (project.SiteProvisionStatus === SiteProvisionStatus.Idle) {
        if (project.ArchiveDate === null) {
          return true;
        }
      }
    }

    return false;
  }

  canArchiveProject(project: Project) {
    const status = project.ProjectStatus ? project.ProjectStatus.toLowerCase() : '';

    if (project.O365GroupId !== null) {
      if (project.SiteProvisionStatus === SiteProvisionStatus.Closed) {
        return true;
      }
    }

    return false;
  }

  onChangePage(pageEvent: PageEvent) {
    if (pageEvent.pageSize !== this.currentPageSize) {
      this.currentPageSize = pageEvent.pageSize;
      localStorage.setItem("projectListPageSize", this.currentPageSize.toString());
    }
  }


  toggleAdvancedSearch() {
    this.showAdvancedSearch === !this.showAdvancedSearch;
    this.projectsDataSource.data = [];
    if(this.showAdvancedSearch === false) {
      this.clearSearch();
    } else {
      // if(this.checkIfCanSubmitAdvancedSearch()){
      //   this.fetchProjects();    
      // }
    }
  }

  submitAdvancedSearch() {
    this.fetchProjects();
  }

  setSearchFieldManager(event: MatAutocompleteSelectedEvent): void {
    if(event) {
      this.searchFieldManager = event.option.value;
    } else {
      this.searchFieldManager = undefined;
      this.managerControl.setValue('');  // clear input box
    }
    this.checkIfCanSubmitAdvancedSearch()
  }

  modelChanged() {
    this.checkIfCanSubmitAdvancedSearch()
  }

  checkIfCanSubmitAdvancedSearch() {
    this.advancedSearchButtonEnabled = 
      !!this.searchFieldProjectId ||
      !!this.searchFieldName ||
      !!this.searchFieldDescription ||
      !!this.searchFieldCostCenter ||
      !!this.searchFieldCustomer ||
      !!this.searchFieldInfoClass ||
      !!this.searchFieldRetention ||
      !!this.searchFieldStartDateFrom ||
      !!this.searchFieldStartDateTo ||
      !!this.searchFieldEndDateFrom ||
      !!this.searchFieldEndDateTo ||
      !!this.searchFieldActivityStatus ||
      !!this.searchFieldHierarchical ||
      !!this.searchFieldManager;
    return this.advancedSearchButtonEnabled;
  }

}



